import React, { useState, useEffect } from 'react'
import { match, P } from 'ts-pattern'
import { ConfigurableText } from '@/app/components'
import Collapsible from '@/components/collapsibleList'
import { CollapsibleListItemHeader } from '@/app/pages/schedule/vehicle/components/CollapsibleListItemHeader'
import Icon from '@/components/Icon'
import { Children } from 'react'
import { Tag } from '@/components'
import DataUsageModal from './DataUsageModal'
import { PermissionType, getConfigStatus } from '../utils/permissions'
import { useIdealPermissionsFromAuthorizationUrl } from '../utils/permissions.hooks'
import { TeslaFleetAuthenticationScope } from '@/types/teslaFleetAuthentication'

const List = (props: { title?: string; children: React.ReactNode }) => {
  return (
    <div className="flex flex-col gap-2 mb-2 only:mb-0">
      {props.title && (
        <ConfigurableText variant="body4">{props.title}</ConfigurableText>
      )}
      <ul className="w-full pl-6 list-disc list-outside">
        {Children.map(props.children, (child) => (
          <li className="w-full">{child}</li>
        ))}
      </ul>
    </div>
  )
}

const ComingSoonBadge = () => (
  <Tag color="blue-100" className="px-1 py-0.5 !white-space-nowrap">
    <ConfigurableText
      variant="body5"
      className="text-xs font-bold text-blue-500"
    >
      COMING SOON
    </ConfigurableText>
  </Tag>
)

export const useViewModel = ({
  missingScopes,
  idealPermissions,
}: {
  missingScopes: TeslaFleetAuthenticationScope[]
  idealPermissions: PermissionType[]
}) => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({})
  const [dataUsageModalOpen, setDataUsageModalOpen] = useState(false)

  const configStatus = getConfigStatus(missingScopes)

  useEffect(() => {
    const initialOpenState: Record<string, boolean> = {}
    idealPermissions.forEach((permission) => {
      initialOpenState[permission] = !configStatus[permission]
    })
    setOpenItems(initialOpenState)
  }, [])

  const getPermissionDescription = (permission: PermissionType) => {
    return match(permission)
      .with('Profile Information', () => [
        'Personalized communication',
        'Offers',
      ])
      .with('Vehicle Information', () => [
        'Save Money or Save The Planet',
        'Scheduled Departure',
        'Trips',
      ])
      .with('Vehicle Location', () => [
        'Manage Charging',
        'Save Money or Save The Planet',
        'Scheduled Departure',
        'Trips',
        'Climate Preconditioning',
      ])
      .with('Vehicle Commands', () => ['Climate Preconditioning'])
      .with('Vehicle Charging Management', () => [
        'Manage Charging',
        'Save Money or Save The Planet',
        'Scheduled Departure',
        'Trips',
      ])
      .with('Energy Product Information', () => [
        'Enroll your Powerwall(s) in demand response (DR) programs',
      ])
      .with('Energy Product Settings', () => [
        'Enroll your Powerwall(s) in demand response (DR) programs',
      ])
      .exhaustive()
  }

  const getFutureFeatureDescription = (permission: PermissionType) => {
    return match(permission)
      .with('Energy Product Information', () => [
        'Save Money or Save The Planet',
      ])
      .with('Energy Product Settings', () => ['Save Money or Save The Planet'])
      .otherwise(() => undefined)
  }

  const toggleItem = (permission: string, open: boolean) => {
    setOpenItems((prev) => ({
      ...prev,
      [permission]: open,
    }))
  }

  const openDataUsageModal = () => setDataUsageModalOpen(true)
  const closeDataUsageModal = () => setDataUsageModalOpen(false)

  return {
    configStatus,
    openItems,
    dataUsageModalOpen,
    getPermissionDescription,
    getFutureFeatureDescription,
    toggleItem,
    openDataUsageModal,
    closeDataUsageModal,
    missingScopes,
  }
}

interface PermissionSectionProps {
  open: boolean
  authorizationUrl: string | null
  missingScopes: TeslaFleetAuthenticationScope[]
  comingSoonPermissions: PermissionType[]
  idealPermissions?: PermissionType[]
  suggestedPermissions?: PermissionType[]
  requiredPermissions?: PermissionType[]
  collapsible?: boolean
  header: React.ReactNode
}

export function PermissionSection(props: PermissionSectionProps) {
  const authorizationUrl = props.authorizationUrl
  const idealPermissionsFromUrl =
    useIdealPermissionsFromAuthorizationUrl(authorizationUrl)
  const idealPermissions = props.idealPermissions ?? idealPermissionsFromUrl
  const open = props.open
  const collapsible = props.collapsible ?? true

  const {
    configStatus,
    openItems,
    dataUsageModalOpen,
    getPermissionDescription,
    getFutureFeatureDescription,
    toggleItem,
    openDataUsageModal,
    closeDataUsageModal,
  } = useViewModel({
    missingScopes: props.missingScopes,
    idealPermissions,
  })

  return (
    <div className="flex flex-col gap-4">
      <DataUsageModal open={dataUsageModalOpen} onClose={closeDataUsageModal} />
      <Collapsible.List collapsible={collapsible}>
        <Collapsible.ListItem
          key="vehicle-label"
          isOpen={open}
          header={props.header}
        >
          <Collapsible.List
            className={`border-0 rounded-t-none ${
              idealPermissions.length > 0 ? 'border-t-[1px]' : ''
            }`}
          >
            {idealPermissions.map((permission) => {
              const hasPermission = configStatus[permission]
              const initiallyOpen = !hasPermission
              const isComingSoon =
                props.comingSoonPermissions.includes(permission)

              return (
                <Collapsible.ListItem
                  key={permission}
                  isOpen={initiallyOpen}
                  header={
                    <CollapsibleListItemHeader
                      title={permission}
                      status={hasPermission ? 'success' : 'failure'}
                    />
                  }
                  onToggle={(open) => toggleItem(permission, open)}
                >
                  <ConfigurableText variant="body4">
                    {match(hasPermission)
                      .with(
                        true,
                        () => 'You have access to features including:',
                      )
                      .with(
                        false,
                        () =>
                          'Please upgrade your permission here so you can access features including:',
                      )
                      .exhaustive()}
                  </ConfigurableText>
                  <List>
                    {getPermissionDescription(permission).map(
                      (feature, index) => (
                        <ConfigurableText key={index} variant="body4">
                          {feature}
                        </ConfigurableText>
                      ),
                    )}
                  </List>
                  {match(getFutureFeatureDescription(permission))
                    .with(P.not(P.nullish), (features) => {
                      return (
                        <>
                          <ConfigurableText variant="body4">
                            Future features include:
                          </ConfigurableText>
                          <List>
                            {features.map((feature, index) => (
                              <ConfigurableText key={index} variant="body4">
                                {feature}
                              </ConfigurableText>
                            ))}
                          </List>
                        </>
                      )
                    })
                    .otherwise(() => undefined)}

                  {permission === 'Vehicle Charging Management' && (
                    <div className="flex flex-row items-center gap-1 mt-3">
                      <Icon
                        name="Info"
                        className="w-4 h-4 cursor-pointer text-themed-blue-500"
                        onClick={openDataUsageModal}
                      />
                      <ConfigurableText variant="body3">
                        How we will be using your data.
                        <span
                          className="ml-1 underline cursor-pointer text-themed-blue-500"
                          onClick={openDataUsageModal}
                        >
                          Learn More
                        </span>
                      </ConfigurableText>
                    </div>
                  )}

                  {isComingSoon && <ComingSoonBadge />}
                </Collapsible.ListItem>
              )
            })}
          </Collapsible.List>
        </Collapsible.ListItem>
      </Collapsible.List>
    </div>
  )
}

export default PermissionSection
